import React, { useMemo } from "react";
import CircularProgress from "./../../components/CircularProgress";
import CustomLineChart from "./../../components/CustomLineChart";
import CustomSwitch from "./../../components/CustomSwitch";
import CustomTable from "./../../components/CustomTable";
import Indicator from "./../../components/Indicator";
import Divider from "./../../components/Divider";
import Footer from "./Footer";
import { parseEditedHTML } from "../../../exportreport/utils";
import { _capitalizeText, _formatNumber } from "../../../../../../shared/helpers/utils";
import { SE_BEDROOMS, SE_SEGMENTS, SE_SOURCE_CREDIT, _sePctChange } from "../../../../../../shared/helpers/streetEasy";
import CustomSkeleton from "./Skeleton";
import ArrowUpIcon from "mdi-react/ArrowUpBoldIcon";
import ArrowDownIcon from "mdi-react/ArrowDownBoldIcon";
import FlatIcon from "mdi-react/MinusIcon";

/**
 * Single-page rental market overview, built on StreetEasy's monthly market data.
 *
 * Every figure is an ASKING rent on listings that were on the market during one month — not a
 * signed lease, not a sale. The comparison is that month against the same month a year earlier,
 * because the source publishes monthly snapshots and nothing finer.
 */
const RentalTypePage = (props) => {
  const { reportData, customDate, AiResponse, editedContent = "", segment = SE_SEGMENTS[0] } = props;
  const isOverview = segment.key === "all_rentals";
  // Metric suffix for this page: `_all` on the overview, `_studio` / `_one_bd` / ... on a bedroom page.
  const f = (base) => `${base}_${segment.field}`;

  const editedMap = useMemo(function () {
    return parseEditedHTML(editedContent);
  }, [editedContent]);

  function renderEditedOrDefault(id, fallback) {
    if (!editedMap || !editedMap[id]) return fallback;
    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(editedMap[id], "text/html");
      var el = doc.body.firstElementChild;
      if (el && el.getAttribute("data-changed") === "true") {
        return <div dangerouslySetInnerHTML={{ __html: el.outerHTML }} />;
      }
    } catch (e) {
      console.warn("Failed to parse edited HTML for id:", id, e);
    }
    return fallback;
  }

  const ns = (localId) => segment.key + "-" + localId;

  const { theme } = customDate;
  const current = reportData && reportData.current;
  const prior = reportData && reportData.priorYear;

  if (!current) {
    return (
      <div className="report-container page-section">
        <section className="report-section">
          <h3 className="header-text heading-location">
            {(reportData && reportData.areaLabel) || "New York City"}
          </h3>
          <div className="header-subtext__heading">{segment.name}</div>
          <p className="rental-narrative">
            StreetEasy does not publish rental market data for this geography.
          </p>
        </section>
        <Footer note={SE_SOURCE_CREDIT} />
      </div>
    );
  }

  const currentPeriodName = reportData.currentMonthLabel;
  const priorYearPeriodName = reportData.priorMonthLabel;

  const money = (v) =>
    v > 0 ? "$" + Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "--";
  const count = (v) => (v > 0 ? _formatNumber(Math.round(v)) : "--");

  const val = (field) => (current[field] != null ? current[field] : null);
  const priorVal = (field) => (prior && prior[field] != null ? prior[field] : null);
  // CircularProgress draws its arc from a NUMBER and falls back to an empty "--" dial for anything
  // else, so the raw change goes to the dial and the formatted string only to text.
  const pctNum = (field) => _sePctChange(val(field), priorVal(field));
  /** Percent-change table cell: direction arrow, coloured green up / red down. */
  const pctCell = (field) => {
    const change = pctNum(field);
    if (change === null) return "--";
    const dir = change > 0 ? "up" : change < 0 ? "down" : "flat";
    return (
      <span className={`rental-pct rental-pct--${dir}`}>
        {dir === "up" ? <ArrowUpIcon /> : dir === "down" ? <ArrowDownIcon /> : <FlatIcon />}
        {Math.abs(change)}%
      </span>
    );
  };

  const medRent = val(f("median_asking_rent"));
  const supply = val(f("rental_inventory"));

  const metricRow = (label, suffix) => [
    label,
    money(val(`median_asking_rent_${suffix}`)),
    money(priorVal(`median_asking_rent_${suffix}`)),
    pctCell(`median_asking_rent_${suffix}`),
    count(val(`rental_inventory_${suffix}`)),
    count(priorVal(`rental_inventory_${suffix}`)),
    pctCell(`rental_inventory_${suffix}`),
  ];

  const pctCellFor = (cur, prior) => {
    const change = _sePctChange(cur, prior);
    if (change === null) return "--";
    const dir = change > 0 ? "up" : change < 0 ? "down" : "flat";
    return (
      <span className={`rental-pct rental-pct--${dir}`}>
        {dir === "up" ? <ArrowUpIcon /> : dir === "down" ? <ArrowDownIcon /> : <FlatIcon />}
        {Math.abs(change)}%
      </span>
    );
  };

  /**
   * Bedroom pages compare this unit size across geographies — a borough report against the other
   * boroughs, a neighborhood against its borough and the city. The rent and supply levels are
   * already on the dials above, so repeating them as a two-row table said nothing new; where this
   * size sits against the rest of the map does.
   */
  const comparisonRows = (reportData.comparison || []).map((row) => {
    const cur = row.current;
    const pri = row.prior;
    const name = _capitalizeText(cur.area_type === "city" ? "New York City" : cur.area_name);
    return [
      name,
      money(cur[f("median_asking_rent")]),
      money(pri && pri[f("median_asking_rent")]),
      pctCellFor(cur[f("median_asking_rent")], pri && pri[f("median_asking_rent")]),
      count(cur[f("rental_inventory")]),
      count(pri && pri[f("rental_inventory")]),
      pctCellFor(cur[f("rental_inventory")], pri && pri[f("rental_inventory")]),
    ];
  });

  // Short, single-row headers. The periods themselves are stated in the subhead above the table,
  // so the columns only need to say which metric and whether it is the prior year — long labels
  // like "SUPPLY July 2025" wrapped.
  // Same shape as the overview table: current, prior year and change for both metrics.
  const segmentTableHeaders = [
    reportData.area && reportData.area.area_type === "neighborhood" ? "AREA" : "BOROUGH",
    "RENT",
    "RENT PRIOR",
    "RENT %",
    "SUPPLY",
    "SUPPLY PRIOR",
    "SUPPLY %",
  ];

  // The overview breaks the market down by unit size.
  const tableRows = isOverview
    ? SE_BEDROOMS.map((bed) => metricRow(bed.label, bed.key))
    : comparisonRows;

  // Plots whichever interval the reader chose — 67 months, 23 quarters or 6 years.
  const chartSeries = (reportData.series || [])
    .filter((bucket) => bucket.stats && bucket.stats[f("median_asking_rent")] > 0)
    .map((bucket) => ({
      year: bucket.label,
      price: Math.round(bucket.stats[f("median_asking_rent")]),
    }));

  // Five years of monthly snapshots is ~60 angled labels, which overlap into an unreadable band.
  // Thin them to roughly a dozen; the line still plots every month.
  const chartLabelInterval = Math.max(0, Math.ceil(chartSeries.length / 12) - 1);

  return (
    <div className="report-container page-section">
      <section className="report-section">
        {/* Same three tiers as the sales Market Report: geography, then the segment as the big
            bold line, then its long form beside the period. */}
        {renderEditedOrDefault(
          ns("headingLocation"),
          <h3 id={ns("headingLocation")} className="header-text heading-location editable track-original">
            {reportData.areaLabel}
          </h3>
        )}

        {renderEditedOrDefault(
          ns("headerSubheading"),
          <div id={ns("headerSubheading")} className="header-subtext__heading editable track-original">
            {segment.name}
          </div>
        )}

        {reportData.requestedMoreSpecific && (
          <p className="rental-scope-note">
            StreetEasy does not publish data for the selected geography; figures cover {reportData.areaLabel}.
          </p>
        )}

        <div className="subtext-grid">
          {renderEditedOrDefault(
            ns("headerInnerSubheading"),
            <div id={ns("headerInnerSubheading")} className="header-inner-subtext__heading editable track-original">
              {segment.title}
            </div>
          )}
          <div>
            <div>Month</div>
            {renderEditedOrDefault(
              ns("dateRange"),
              <div id={ns("dateRange")} className="editable track-original">
                {priorYearPeriodName} vs. {currentPeriodName}
              </div>
            )}
          </div>
        </div>

        {/* Asking rent leads, with its year-over-year move. */}
        <div className="semi-progress-container">
          <div className="inner_heading_container">
            <div>Median Rent</div>
            <CircularProgress value={pctNum(f("median_asking_rent"))} />
          </div>
          <CustomSwitch
            rows={[
              { firstValue: "Median Rent", middleValue: "", lastValue: "Supply", header: true },
              { firstValue: money(medRent), middleValue: currentPeriodName, lastValue: count(supply) },
              {
                firstValue: money(priorVal(f("median_asking_rent"))),
                middleValue: priorYearPeriodName,
                lastValue: count(priorVal(f("rental_inventory"))),
              },
            ]}
            hightlightedElement={1}
          />
          <div className="inner_heading_container">
            <div>Supply</div>
            <div>
              <CircularProgress value={pctNum(f("rental_inventory"))} />
            </div>
          </div>
        </div>

        <div className="report-layout-table-container rental-table-container">
          <CustomTable
            headers={
              isOverview
                ? ["BEDROOM", "RENT", "RENT PRIOR", "RENT %", "SUPPLY", "SUPPLY PRIOR", "SUPPLY %"]
                : segmentTableHeaders
            }
            rows={tableRows}
          />
        </div>

        {renderEditedOrDefault(
          ns("rentsNarrative"),
          <p id={ns("rentsNarrative")} className="rental-narrative editable track-original">
            {AiResponse && AiResponse[segment.key] && AiResponse[segment.key].rents ? (
              AiResponse[segment.key].rents
            ) : (
              <CustomSkeleton count={12} />
            )}
          </p>
        )}
      </section>

      <Divider theme={theme} />

      <section className="report-section">
        <div className="custom-charts">
          <div className="indicator-container">
            <div className="heading">RENTS</div>
            <Indicator
              price={money(medRent)}
              percentage={pctNum(f("median_asking_rent"))}
              reverse={true}
              name="Med Rent"
            />
            {/* No days-on-market indicator: StreetEasy publishes that for sales only. */}
            <Indicator
              price={count(supply)}
              percentage={pctNum(f("rental_inventory"))}
              reverse={true}
              name="Supply"
            />
          </div>

          <div className="chart_wrapper">
            <div className="chart_inner">
              <div className="Chart-1 Has-Loc">
                <div className="heading">MEDIAN RENT{isOverview ? "" : " — " + segment.label.toUpperCase()}</div>
                {chartSeries.length > 0 ? (
                  <CustomLineChart
                    theme={theme}
                    data={chartSeries}
                    preformattedLabels
                    xInterval={chartLabelInterval}
                    yTickStep={250}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer
        note={`${SE_SOURCE_CREDIT} ${
          reportData.isAveraged
            ? `${currentPeriodName} figures are averages of that period's monthly values — rents are averages of monthly medians, supply is average listings on the market.`
            : `Median asking rents on listings active in ${currentPeriodName}.`
        }`}
      />
    </div>
  );
};

export default RentalTypePage;
